import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n';
import TagManager from 'react-gtm-module';

import FormContextProvider from '../context/form-context';
import api from '../api';
import config from '../config';
import { getTimeoutDuration } from '../selectors/settings';
import { getAllModulesWithProgress } from '../selectors/progress';
import { getModuleListError, getModuleListLoading } from '../selectors/core';
import { getVideos, getVideosError, getVideosLoading } from '../selectors/videos';

import useAutoLogout from '../hooks/useAutoLogout';
import PrivateRoute from '../components/PrivateRoute';
import HeaderContainer from './HeaderContainer';
import FooterContainer from './FooterContainer';
import LoginContainer from './LoginContainer';
import LoadingSpinner from '../components/LoadingSpinner';

const ProfileContainer = lazy(() => import('./ProfileContainer'));
const LearnIndexContainer = lazy(() => import('./Lessons/LearnIndexContainer'));
const DashboardContainer = lazy(() => import('./dashboard/DashboardContainer'));
const DiaryContainer = lazy(() => import('./DiaryContainer'));
const DiarySetupContainer = lazy(() => import('./DiarySetupContainer'));
const DiaryResetPinContainer = lazy(() => import('./DiaryResetPinContainer'));
const ModuleDetailContainer = lazy(() => import('./ModuleDetailContainer'));
const ModuleIntroPageContainer = lazy(() => import('./ModuleIntroPageContainer'));
const LearnPageContainer = lazy(() => import('./Lessons/LearnPageContainer'));
const DischargePageContainer = lazy(() => import('./Lessons/DischargePageContainer'));
const QuestionViewContainer = lazy(() => import('./QuestionViewContainer'));
const Onboarding = lazy(() => import('../components/Onboarding'));
const Glossary = lazy(() => import('../components/Glossary'));
const SubpageContainer = lazy(() => import('./SubpageContainer'));
const ResetPasswordContainer = lazy(() => import('./ResetPasswordContainer'));
const DischargePathwayViewContainer = lazy(() => import('./DischargePathwayViewContainer'));
const AccountOptions = lazy(() => import('../components/AccountOptions/AccountOptions'));
const AnalyticsContainer = lazy(() => import('./AnalyticsContainer'));
const TimedOutMessage = lazy(() => import('../components/TimedOutMessage/TimedOutMessage'));
const Videos = lazy(() => import('./Videos/Videos'));
const Video = lazy(() => import('../components/edu/Video'));

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');

  // 1. Safely monitor the browser's network status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const {
    showHeader, showFooter, loggedIn, token,
    setUpAnalytics, analyticsId, timeoutMinutes,
    modules, modulesLoading, modulesError,
    videos, videosLoading, videosError
  } = useSelector(state => ({
    showHeader: state.ui.showHeader,
    showFooter: state.ui.showFooter,
    loggedIn: state.auth.hasToken,
    token: state.auth.token,
    setUpAnalytics: state.analytics.setUpAnalytics,
    analyticsId: state.analytics.analyticsId,
    timeoutMinutes: getTimeoutDuration(state),
    modules: getAllModulesWithProgress(state),
    modulesLoading: getModuleListLoading(state),
    modulesError: getModuleListError(state),
    videos: getVideos(state),
    videosLoading: getVideosLoading(state),
    videosError: getVideosError(state)
  }), shallowEqual); 

  // 2. Reactive Data Initialization
  useEffect(() => {
    if (loggedIn && isOnline && !isOnboardingRoute) {
      if (!timeoutMinutes) {
        dispatch(api.actions.settingsList({})).catch(() => {});
      }
      if (modules.length === 0 && !modulesLoading) {
        dispatch(api.actions.modulesList({})).catch(() => {});
        dispatch(api.actions.dischargeSteps({})).catch(() => {});
        dispatch(api.actions.dischargeContent({})).catch(() => {});
      }
      if (!videos && !videosLoading) {
        dispatch(api.actions.videoList({})).catch(() => {});
      }
      dispatch(api.actions.requestsList({})).catch(() => {});
    }
    // CRITICAL: We intentionally omit modules/videos/settings from this dependency array 
    // to prevent an infinite retry loop if the server is actually down (HTTP 500).
    // This effect will now trigger exactly when the user logs in, and exactly when 
    // their device transitions from offline back to online.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, isOnline, location.pathname, dispatch]); 

  useAutoLogout(loggedIn, token, navigate, timeoutMinutes);

  useEffect(() => {
    document.body.classList.remove('navigation-dropdown--open');
  }, [location]);

  useEffect(() => {
    if (setUpAnalytics && analyticsId) {
      TagManager.initialize({
        gtmId: 'GTM-M5398JV',
        auth: config.gtm.auth,
        dataLayer: { userId: analyticsId }
      });
    }
  }, [analyticsId, setUpAnalytics]);

  return (
    <I18nextProvider i18n={i18n}>
      <FormContextProvider>
        <div className="wrapper">
          {showHeader && !isOnboardingRoute && <HeaderContainer />}

          <main className={`content-container ${showHeader ? '' : '--no-padding'}`} role="main">
            <Suspense fallback={<LoadingSpinner size="large" colorScheme="blue" />}>
              <Routes>
                <Route path="/" element={<Navigate to="/subpage/home" replace />} />
                
                <Route path="/subpage/:page" element={<SubpageContainer />} />
                <Route path="/login" element={<LoginContainer />} />
                <Route path="/timed-out" element={<TimedOutMessage />} />
                <Route path="/onboarding/*" element={<Onboarding />} />
                
                <Route path="/password/reset/:resetPasswordToken" element={<ResetPasswordContainer />} />
                <Route path="/diary/reset/:resetPinToken" element={<DiaryResetPinContainer />} />

                <Route path="/glossary" element={<PrivateRoute><Glossary /></PrivateRoute>} />
                
                <Route path="/dashboard/*" element={<PrivateRoute><DashboardContainer modules={modules} loading={modulesLoading} error={modulesError} /></PrivateRoute>} />
                
                <Route path="/profile" element={<PrivateRoute><ProfileContainer /></PrivateRoute>} />
                <Route path="/diary" element={<PrivateRoute><DiaryContainer /></PrivateRoute>} />
                <Route path="/diary/setup" element={<PrivateRoute><DiarySetupContainer /></PrivateRoute>} />
                <Route path="/questions" element={<PrivateRoute><QuestionViewContainer /></PrivateRoute>} />
                
                <Route path="/videos" element={<PrivateRoute><Videos videos={videos} loading={videosLoading} error={videosError} /></PrivateRoute>} />
                <Route path="/videos/:videoSlug" element={<PrivateRoute><Video videos={videos} loading={videosLoading} /></PrivateRoute>} />

                <Route path="/learn" element={<PrivateRoute><LearnIndexContainer /></PrivateRoute>} />
                <Route path="/learn/chapter/:moduleSlug/pages/:pageSlug" element={<PrivateRoute><ModuleIntroPageContainer /></PrivateRoute>} />
                <Route path="/learn/chapter/:moduleSlug/lessons/lesson/:lessonSlug/part/:partSlug" element={<PrivateRoute><LearnPageContainer modules={modules} /></PrivateRoute>} />
                <Route path="/learn/chapter/:moduleSlug" element={<PrivateRoute><ModuleDetailContainer /></PrivateRoute>} />

                <Route path="/discharge" element={<PrivateRoute><DischargePathwayViewContainer /></PrivateRoute>} />
                <Route path="/discharge/:moduleSlug/lessons/:lessonSlug/part/:partSlug" element={<PrivateRoute><DischargePageContainer /></PrivateRoute>} />

                <Route path="/options/:optionTab?" element={<PrivateRoute><AccountOptions /></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><AnalyticsContainer /></PrivateRoute>} />

                <Route path="*" element={<SubpageContainer />} />
              </Routes>
            </Suspense>
          </main>

          {showFooter && !isOnboardingRoute && <FooterContainer />}
        </div>
      </FormContextProvider>
    </I18nextProvider>
  );
};

export default App;